<template>
  <BoxWithTitle
    v-if="!loading"
    collapsible
    openingIcon="mdi-chevron-down"
    closingIcon="mdi-chevron-up"
  >
    <template #header>
      <v-toolbar-title>
        {{ $t('members.memberAccess') }}
      </v-toolbar-title>
    </template>

    <template #content>
      <form>
        <div v-if="!isOperateur(member)">
          <v-col
            cols="12"
            sm="4"
          >
            <VCheckbox
              v-model="canAccessAllClients"
              :disabled="isAdminOrManager(member)"
              :label="$t('forms.labels.canAllAccessClients')"
              @click.native="UpdateAccessToAllClientStatus()"
            />
          </v-col>
        </div>
        <div v-if="isOperateur(member) || !canAccessAllClients">
          <v-col cols="11">
            <AccessGroupForm
              ref="mainForm"
              :accessGroups="updatedData"
              @dirtyChanged="isFormDirty = $event"
              @validChanged="isFormValid = $event"
              @sendData="updateData"
            />
          </v-col>
          <v-row
            v-if="hasPermission('members.accessGroups.view')"
            align="center"
            justify="end"
            class="mr-1"
          >
            <v-btn
              :disabled="!isFormValid || !isFormDirty"
              type="submit"
              color="secondary"
              @click.prevent="onClickUpdate"
            >
              {{ $t('forms.btn.save') }}
            </v-btn>
          </v-row>
        </div>
      </form>
      <MemberAssociatedClients
        v-if="hasPermission('members.clients.view') && !canAccessAllClients && (hasRole('manager') || hasRole('administrator'))"
        @canAllAccessClients="canAllAccessClients"
      />
    </template>
  </BoxWithTitle>
</template>
<script>
import { mapGetters, mapActions } from 'vuex';
import BoxWithTitle from '@/components/shared/containers/VBoxWithTitle.vue';
import FormPageMixin from '@/mixins/FormPageMixin';
import AccessGroupForm from '@/components/forms/AccessGroupForm';
import MemberAssociatedClients from '@/views/member/MemberAssociatedClients.vue';
import VCheckbox from '@/components/shared/widgets/forms/VuetifyInputs/VCheckbox.vue';
import roles from '@/utilities/enums/rolesTypes';

export default {
    components: {
        BoxWithTitle,
        AccessGroupForm,
        MemberAssociatedClients,
        VCheckbox,
    },
    mixins: [
        FormPageMixin,
    ],
    props: {
        updatedRole: {
            type: Boolean,
            default: false,
        },
        resetRoleChanged: {
            type: Function,
            default: () => null,
        },
    },
    data() {
        return {
            accessGroups: [],
            access: false,
            hideAccess: false,
            canAccessAllClients: false,
            loading: false,
        };
    },
    computed: {
        ...mapGetters([
            'member',
            'hasPermission',
            'hasRole',
        ]),
        defaultData() {
            return this.accessGroups;
        },
    },
    watch: {
        updatedRole: {
            async handler() {
                if (this.updatedRole) {
                    this.loading = true;
                    await this.getMemberAccessGroups();
                    await this.getMemberInfo(this.member.id);
                    await this.resetRoleChanged();
                    this.loading = false;
                }
            },
            immediate: true,
        },

    },
    async created() {
        await this.getMemberAccessGroups();

        // If the store member is updated, re-fetch the member access groups.
        // Note, watch does not work in this case:
        this.unsubscribe = this.$store.subscribe(async mutation => {
            if (mutation.type === 'setMemberState') await this.getMemberAccessGroups();
        });
    },
    beforeDestroy() {
        // Unsubscribe from member changes on unmount:
        this.unsubscribe();
    },
    methods: {
        ...mapActions({
            getMemberInfo: 'getMemberInfo',
        }),
        async getMemberAccessGroups() {
            const { data } = await this.$rq.getAccessGroups({ memberId: this.member.id, pagination: false });
            this.accessGroups = data.accessGroups.map(({ id }) => id);
            this.canAccessAllClients = this.member.canAccessAllClients;
            this.showAccess();
            this.initFormData();
        },
        async onClickUpdate() {
            const { id, firstName, lastName } = this.member;

            await this.$rq.updateMemberAccessGroup(id, { accessGroups: this.updatedData });
            await this.getMemberAccessGroups();

            this.cancelModifications();
            this.$toasted.global.toast_success({
                message: this.$t('msg.accessGroupsUpdated', {
                    data: `${firstName} ${lastName}`,
                }),
            });
        },
        canAllAccessClients(val) {
            this.access = val;
        },
        showAccess() {
            if (this.canAccessAllClients) {
                this.hideAccess = true;
            } else {
                this.hideAccess = false;
            }
        },
        async UpdateAccessToAllClientStatus() {
            this.showAccess();
            const { firstName, lastName } = this.member;
            await this.$rq.UpdateCanAccessToAllClients(this.member.id, { canAccessAllClients: this.canAccessAllClients });
            await this.getMemberInfo(this.member.id);
            this.$toasted.global.toast_success({
                message: this.$t('msg.canAccessToAllClientsUpdated', {
                    data: `${firstName} ${lastName}`,
                }),
            });
        },
        isOperateur(member) {
            return member.roles[0].key === (roles.OPERATOR);
        },
        isAdminOrManager(member) {
            return member.roles[0].key === (roles.ADMINISTRATOR)
          || member.roles[0].key === (roles.MANAGER)
          || member.roles[0].key === (roles.LABMANAGER);
        },
    },
};
</script>
